<script>
import _ from 'lodash'
import CustomTooltip from '@/components/booking/bookingList/custom-tooltip.vue'
import BaseCheckbox from '@/components/_base-checkbox.vue'
import BaseButton from '@/components/_base-button.vue'
import { mapGetters, mapActions } from 'vuex'
import baseMixin from '@/mixins/baseMixin'

export default {
  name: 'TheClientFilter',
  components: { CustomTooltip, BaseCheckbox, BaseButton },
  mixins: [baseMixin],
  props: {
    right: {
      type: Boolean,
    },
  },
  emits: ['toggle', 'update'],
  data() {
    return {
      showDropdown: false,
      searchClientQuery: '',
      localSelectedClientIds: [],
      scrollAreaHeight: 0,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      clientList: 'auth/getAllClients',
    }),
    hasSelectedClients() {
      return this.localSelectedClientIds.length > 0
    },
    selectedClientCount() {
      return this.getSelectedClients.length
    },
    sortedClients() {
      return [...this.clientList].sort((a, b) =>
        a.clientName.localeCompare(b.clientName)
      )
    },
    selectedClients() {
      return this.sortedClients.filter((client) =>
        this.localSelectedClientIds.includes(client.clientId)
      )
    },
    unselectedClients() {
      return this.sortedClients.filter(
        (client) => !this.localSelectedClientIds.includes(client.clientId)
      )
    },
    filteredSelectedClients() {
      return this.filterBySearch(this.selectedClients)
    },
    filteredUnselectedClients() {
      return this.filterBySearch(this.unselectedClients)
    },
  },
  watch: {
    getSelectedClients: {
      immediate: true,
      handler(newVal) {
        this.localSelectedClientIds = [...newVal]
      },
    },
    showDropdown(val) {
      if (val) {
        this.$nextTick(() => {
          this.updateScrollAreaHeight()
        })

        this.hydrateLocalSelectedClientIds()
      }
    },
  },
  created() {
    this.updateScrollAreaHeight = _.throttle(this.updateScrollAreaHeight, 100)
  },
  mounted() {
    this.hydrateLocalSelectedClientIds()
    document.addEventListener(
      'click',
      this.handleClickOutsideClientFilter,
      true
    )
    window.addEventListener('resize', this.updateScrollAreaHeight)
  },
  beforeUnmount() {
    document.removeEventListener(
      'click',
      this.handleClickOutsideClientFilter,
      true
    )
    window.removeEventListener('resize', this.updateScrollAreaHeight)
  },
  methods: {
    ...mapActions({
      setClientList: 'client/setClientList',
    }),
    hydrateLocalSelectedClientIds() {
      this.localSelectedClientIds = [...this.getSelectedClients]
    },
    getEventPath(event) {
      if (typeof event.composedPath === 'function') {
        return event.composedPath()
      }
      // Fallback for older browsers
      const path = []
      let node = event.target
      while (node) {
        path.push(node)
        node = node.parentNode || node.host
      }
      path.push(window)
      return path
    },
    handleClickOutsideClientFilter(e) {
      const path = this.getEventPath(e)
      const refsToCheck = ['clientContainer']

      const clickedInsideRef = refsToCheck.some((ref) => {
        const el = this.$refs[ref]
        return el && path.includes(el)
      })

      const clickedOnTrigger = path.some(
        (node) => node instanceof HTMLElement && node.closest('.client-filter')
      )

      const inOverlay = path.some(
        (node) =>
          node instanceof HTMLElement &&
          (node.closest('.v-menu__content') ||
            node.closest('.v-overlay__content'))
      )

      if (!clickedInsideRef && !clickedOnTrigger && !inOverlay) {
        this.showDropdown = false
      }
    },
    toggleClientFilter() {
      this.showDropdown = !this.showDropdown
      this.$emit('toggle')
    },
    filterBySearch(clients) {
      const query = (this.searchClientQuery || '').toLowerCase()
      if (!query) return clients
      return clients.filter((client) =>
        (client.clientName || '').toLowerCase().includes(query)
      )
    },
    toggleClientSelection(clientId, isChecked) {
      if (isChecked) {
        if (!this.localSelectedClientIds.includes(clientId)) {
          this.localSelectedClientIds.push(clientId)
        }
      } else {
        this.localSelectedClientIds = this.localSelectedClientIds.filter(
          (id) => id !== clientId
        )
      }
    },
    handleClientClick(client, isSelected) {
      this.toggleClientSelection(client.clientId, !isSelected)
    },
    hideAllSelectedClients() {
      const selectedIds = this.filteredSelectedClients.map((c) => c.clientId)
      this.localSelectedClientIds = this.localSelectedClientIds.filter(
        (id) => !selectedIds.includes(id)
      )
    },
    showAllUnselectedClients() {
      const unselectedIds = this.filteredUnselectedClients.map(
        (c) => c.clientId
      )
      this.localSelectedClientIds = [
        ...new Set([...this.localSelectedClientIds, ...unselectedIds]),
      ]
    },
    updateScrollAreaHeight() {
      this.$nextTick(() => {
        const container = this.$refs.clientContainer
        if (!container) return

        const topOffset = container.getBoundingClientRect().top
        const marginBottom = 40
        this.scrollAreaHeight = window.innerHeight - topOffset - marginBottom
      })
    },
    emitSelection() {
      this.setClientList(this.localSelectedClientIds)
      this.$emit('update', [...this.localSelectedClientIds])
      this.showDropdown = false
    },
  },
}
</script>

<template>
  <div style="position: relative" class="top-filter-bar">
    <!-- Toggle button -->
    <CustomTooltip
      position="bottom"
      class="client-filter"
      :class="{ active: showDropdown }"
      :text="$t('topFilterBar.client.title')"
    >
      <div class="cursor-pointer" @click.stop="toggleClientFilter">
        <v-icon :class="{ 'me-2': !isMobile }">mdi-account-outline</v-icon>
        <span v-if="!isMobile" class="top-filter-title">
          {{
            $tc('topFilterBar.client.showClientsTitle', selectedClientCount, {
              count: selectedClientCount,
            })
          }}
        </span>
      </div>
    </CustomTooltip>

    <!-- Dropdown panel -->
    <div
      v-if="showDropdown"
      ref="clientContainer"
      class="client-container"
      :class="{ 'client-container--right': right }"
      :style="{ maxHeight: `${scrollAreaHeight}px` }"
    >
      <div class="title px-2 mb-3">{{ $t('topFilterBar.client.title') }}</div>

      <!-- Search box -->
      <div class="filter-container-search mb-5">
        <v-icon>mdi-magnify</v-icon>
        <input
          v-model="searchClientQuery"
          class="ps-2 search-input h-full"
          style="width: 100%; outline: none"
          :placeholder="$t('client.clientSelectorSearchInputText')"
        />
      </div>

      <div class="client-scrollable">
        <!-- Selected section -->
        <div
          v-if="filteredSelectedClients.length > 0"
          class="d-flex px-2"
          style="justify-content: space-between"
        >
          <span class="client-showall cursor-pointer">{{
            $t('topFilterBar.client.showing')
          }}</span>
          <span
            class="client-hideall cursor-pointer"
            @click="hideAllSelectedClients"
          >
            {{ $t('topFilterBar.client.hideAll') }}
          </span>
        </div>

        <div v-if="filteredSelectedClients.length > 0" class="mt-5">
          <div
            v-for="client in filteredSelectedClients"
            :key="'selected-' + client.clientId"
            class="mb-1 clients-list d-flex items-start space-x-2"
          >
            <BaseCheckbox
              :value="client.clientId"
              :model-value="localSelectedClientIds.includes(client.clientId)"
              sm
              custom-class="custom-checkbox"
              @update:modelValue="
                (isChecked) => toggleClientSelection(client.clientId, isChecked)
              "
            />
            <span
              class="client-name"
              style="font-weight: 500"
              @click="handleClientClick(client, true)"
            >
              {{ client.clientName }}
            </span>
          </div>
        </div>

        <!-- Unselected section -->
        <div
          v-if="filteredUnselectedClients.length > 0"
          class="d-flex mb-5 px-2"
          style="justify-content: space-between"
        >
          <span class="client-showall cursor-pointer">{{
            $t('topFilterBar.client.hidden')
          }}</span>
          <span
            class="client-hideall cursor-pointer"
            @click="showAllUnselectedClients"
          >
            {{ $t('topFilterBar.client.showAll') }}
          </span>
        </div>

        <div v-if="filteredUnselectedClients.length > 0" class="mt-5">
          <div
            v-for="client in filteredUnselectedClients"
            :key="'unselected-' + client.clientId"
            class="mb-1 clients-list d-flex items-start space-x-2"
          >
            <BaseCheckbox
              :value="client.clientId"
              :model-value="false"
              sm
              custom-class="custom-checkbox"
              @update:modelValue="
                (isChecked) => toggleClientSelection(client.clientId, isChecked)
              "
            />
            <span
              class="client-name"
              style="font-weight: 500"
              @click="handleClientClick(client, false)"
            >
              {{ client.clientName }}
            </span>
          </div>
        </div>
      </div>

      <!-- Apply Button -->
      <div class="client-footer">
        <BaseButton
          border-radius="8px"
          width="70"
          height="30"
          class="apply-btn p-5"
          :disabled="!hasSelectedClients"
          @click.stop="emitSelection"
        >
          {{ $t('general.apply') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/layout/the-client-filter.scss';
</style>
